/**
 * Cell Demo Scene
 * Showcases procedural cell rendering with different cell types
 */

import Phaser from 'phaser';
import { CellAnimationSystem } from '@procedural/graphics';
import { OrganelleType } from '@/types';
import type { ProceduralCellConfig } from '@/types';
import { CELL_COLORS_HSL } from '@/utils/colors';

export class CellDemoScene extends Phaser.Scene {
  private animationSystem!: CellAnimationSystem;
  private currentCellIndex: number = 0;
  private cellConfigs: { name: string; config: ProceduralCellConfig }[] = [];

  constructor() {
    super({ key: 'CellDemoScene' });
  }

  create(): void {
    // Create bioluminescent background
    this.createBackground();

    this.animationSystem = new CellAnimationSystem(this, {
      pulseAmplitude: 0.03,
      pulseSpeed: 0.4,
      driftRadius: 4,
      driftSpeed: 0.4,
      cytoplasmFlow: true,
      speedMultiplier: 1,
    });

    // Define different cell types to showcase
    this.cellConfigs = [
      { name: 'Animal Cell', config: this.createAnimalCellConfig() },
      { name: 'Plant Cell', config: this.createPlantCellConfig() },
      { name: 'Coccus (Spherical Bacteria)', config: this.createCoccusConfig() },
      { name: 'Bacillus (Rod Bacteria)', config: this.createBacillusConfig() },
      { name: 'Spirillum (Spiral Bacteria)', config: this.createSpirillumConfig() },
      { name: 'Paramecium', config: this.createParameciumConfig() },
      { name: 'Amoeba', config: this.createAmoebaConfig() },
    ];

    // Create initial cell
    this.createDemoCell();

    // Start animations
    this.animationSystem.start();

    // Add UI controls
    this.createUI();

    // Add keyboard controls
    this.input.keyboard?.on('keydown-LEFT', () => this.prevCell());
    this.input.keyboard?.on('keydown-RIGHT', () => this.nextCell());
    this.input.keyboard?.on('keydown-SPACE', () => this.regenerateCell());
  }

  override update(_time: number, delta: number): void {
    this.animationSystem.update(delta);
  }

  private createDemoCell(): void {
    const { config, name } = this.cellConfigs[this.currentCellIndex];

    // Remove existing cell
    if (this.animationSystem.cellCount > 0) {
      this.animationSystem.removeAnimatedCell('demo-cell');
    }

    // Create new cell with random seed
    const seed = Date.now();
    this.animationSystem.createAnimatedCell(config, seed, 'demo-cell');

    // Position at center
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    this.animationSystem.positionCell('demo-cell', centerX, centerY);

    // Update label
    this.updateLabel(name);
  }

  private createUI(): void {
    const { width, height } = this.cameras.main;

    // Back button
    this.createBackButton();

    // Title
    this.add.text(width / 2, 40, 'Procedural Cell Demo', {
      fontSize: '32px',
      color: '#E2E8F0',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // Instructions
    this.add.text(width / 2, height - 40, '← → to change cell type | SPACE to regenerate', {
      fontSize: '16px',
      color: '#A0AEC0',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // Cell name label
    const label = this.add.text(width / 2, 90, '', {
      fontSize: '24px',
      color: '#68D391',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    label.setData('type', 'cellLabel');

    // Navigation buttons
    const prevBtn = this.add.text(width / 2 - 150, height - 80, '◀ Prev', {
      fontSize: '20px',
      color: '#4299E1',
      backgroundColor: '#1A365D',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    prevBtn.on('pointerover', () => prevBtn.setColor('#68D391'));
    prevBtn.on('pointerout', () => prevBtn.setColor('#4299E1'));
    prevBtn.on('pointerdown', () => this.prevCell());

    const nextBtn = this.add.text(width / 2 + 150, height - 80, 'Next ▶', {
      fontSize: '20px',
      color: '#4299E1',
      backgroundColor: '#1A365D',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    nextBtn.on('pointerover', () => nextBtn.setColor('#68D391'));
    nextBtn.on('pointerout', () => nextBtn.setColor('#4299E1'));
    nextBtn.on('pointerdown', () => this.nextCell());

    const regenBtn = this.add.text(width / 2, height - 80, '↻ Regenerate', {
      fontSize: '20px',
      color: '#F6E05E',
      backgroundColor: '#744210',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    regenBtn.on('pointerover', () => regenBtn.setColor('#68D391'));
    regenBtn.on('pointerout', () => regenBtn.setColor('#F6E05E'));
    regenBtn.on('pointerdown', () => this.regenerateCell());
  }

  private updateLabel(name: string): void {
    const label = this.children.getAll().find(
      (child) => child.getData('type') === 'cellLabel'
    ) as Phaser.GameObjects.Text | undefined;

    if (label) {
      label.setText(name);
    }
  }

  private prevCell(): void {
    this.currentCellIndex = (this.currentCellIndex - 1 + this.cellConfigs.length) % this.cellConfigs.length;
    this.createDemoCell();
  }

  private nextCell(): void {
    this.currentCellIndex = (this.currentCellIndex + 1) % this.cellConfigs.length;
    this.createDemoCell();
  }

  private regenerateCell(): void {
    this.createDemoCell();
  }

  private createAnimalCellConfig(): ProceduralCellConfig {
    return {
      cellType: 'animal',
      size: { min: 80, max: 120 },
      membrane: {
        baseRadius: 100,
        irregularity: 0.25,
        thickness: 3,
        color: CELL_COLORS_HSL.membrane,
        pulseRate: 1,
        permeability: 0.5,
      },
      nucleus: {
        relativeSize: 0.35,
        position: 'center',
        chromatinDensity: 0.6,
        nucleolusCount: 2,
      },
      organelles: [
        { type: OrganelleType.MITOCHONDRIA, density: 0.7 },
        { type: OrganelleType.RIBOSOME, density: 0.8 },
        { type: OrganelleType.ENDOPLASMIC_RETICULUM, density: 0.6 },
        { type: OrganelleType.VACUOLE, density: 0.3 },
      ],
      cytoplasm: {
        color: CELL_COLORS_HSL.cytoplasm,
        density: 0.5,
      },
      animation: {
        drift: 1,
        pulse: 1,
        morph: 0.5,
      },
    };
  }

  private createPlantCellConfig(): ProceduralCellConfig {
    return {
      cellType: 'plant',
      size: { min: 90, max: 130 },
      membrane: {
        baseRadius: 110,
        irregularity: 0.1, // Plant cells are more rectangular
        thickness: 4,
        color: { h: 120, s: 40, l: 60 }, // Green tint
        pulseRate: 0.5,
        permeability: 0.3,
      },
      nucleus: {
        relativeSize: 0.25,
        position: 'offset', // Pushed to side by central vacuole
        chromatinDensity: 0.5,
        nucleolusCount: 1,
      },
      organelles: [
        { type: OrganelleType.CHLOROPLAST, density: 0.9 },
        { type: OrganelleType.CENTRAL_VACUOLE, density: 1 },
        { type: OrganelleType.MITOCHONDRIA, density: 0.4 },
        { type: OrganelleType.RIBOSOME, density: 0.6 },
      ],
      cytoplasm: {
        color: { h: 120, s: 20, l: 85 },
        density: 0.4,
      },
      animation: {
        drift: 0.3,
        pulse: 0.3,
        chloroplastStream: 1,
      },
    };
  }

  private createCoccusConfig(): ProceduralCellConfig {
    return {
      cellType: 'bacteria',
      shape: 'spherical', // Coccus - spherical/round bacteria
      size: { min: 40, max: 60 },
      membrane: {
        baseRadius: 50,
        irregularity: 0.15,
        thickness: 2,
        color: { h: 45, s: 70, l: 65 }, // Tan color
        pulseRate: 2,
        permeability: 0.7,
      },
      nucleus: null, // Prokaryotes have no nucleus
      organelles: [
        { type: OrganelleType.RIBOSOME, density: 1 },
        { type: OrganelleType.NUCLEOID, density: 0.8 },
      ],
      cytoplasm: {
        color: { h: 45, s: 30, l: 80 },
        density: 0.7,
      },
      animation: {
        drift: 2,
        pulse: 1.5,
      },
    };
  }

  private createBacillusConfig(): ProceduralCellConfig {
    return {
      cellType: 'bacteria',
      shape: 'rod', // Bacillus - rod/capsule shaped bacteria
      size: { min: 80, max: 100 },
      membrane: {
        baseRadius: 90,
        irregularity: 0.1,
        thickness: 2,
        color: { h: 200, s: 60, l: 55 }, // Blue-teal color
        pulseRate: 1.5,
        permeability: 0.7,
      },
      nucleus: null,
      organelles: [
        { type: OrganelleType.RIBOSOME, density: 0.8 },
        { type: OrganelleType.NUCLEOID, density: 1 },
      ],
      cytoplasm: {
        color: { h: 190, s: 40, l: 75 },
        density: 0.6,
      },
      animation: {
        drift: 3,
        pulse: 1.2,
      },
    };
  }

  private createSpirillumConfig(): ProceduralCellConfig {
    return {
      cellType: 'bacteria',
      shape: 'spiral', // Spirillum - corkscrew/helical bacteria
      size: { min: 30, max: 45 },
      membrane: {
        baseRadius: 40,
        irregularity: 0.08,
        thickness: 2,
        color: { h: 320, s: 55, l: 60 }, // Magenta/pink color (like reference image)
        pulseRate: 1,
        permeability: 0.6,
      },
      nucleus: null,
      organelles: [
        { type: OrganelleType.RIBOSOME, density: 0.6 },
        { type: OrganelleType.NUCLEOID, density: 0.8 },
      ],
      cytoplasm: {
        color: { h: 320, s: 30, l: 80 },
        density: 0.5,
      },
      animation: {
        drift: 4,
        pulse: 0.8,
      },
    };
  }

  private createParameciumConfig(): ProceduralCellConfig {
    return {
      cellType: 'protist',
      size: { min: 100, max: 140 },
      membrane: {
        baseRadius: 120,
        irregularity: 0.2,
        thickness: 2,
        color: { h: 180, s: 50, l: 70 }, // Cyan tint
        pulseRate: 0.8,
        permeability: 0.5,
      },
      nucleus: {
        relativeSize: 0.2,
        position: 'center',
        chromatinDensity: 0.7,
        nucleolusCount: 1,
      },
      organelles: [
        { type: OrganelleType.CILIA, density: 1 },
        { type: OrganelleType.CONTRACTILE_VACUOLE, density: 0.8 },
        { type: OrganelleType.FOOD_VACUOLE, density: 0.6 },
        { type: OrganelleType.ORAL_GROOVE, density: 1 },
        { type: OrganelleType.MITOCHONDRIA, density: 0.5 },
      ],
      cytoplasm: {
        color: { h: 180, s: 20, l: 85 },
        density: 0.5,
      },
      animation: {
        drift: 1.5,
        pulse: 0.8,
        cilia: 2,
      },
    };
  }

  private createAmoebaConfig(): ProceduralCellConfig {
    return {
      cellType: 'protist',
      size: { min: 80, max: 120 },
      membrane: {
        baseRadius: 100,
        irregularity: 0.6, // Very irregular, amoeba-like
        thickness: 2,
        color: { h: 270, s: 40, l: 70 }, // Purple tint
        pulseRate: 0.5,
        permeability: 0.6,
      },
      nucleus: {
        relativeSize: 0.25,
        position: 'offset',
        chromatinDensity: 0.5,
        nucleolusCount: 1,
      },
      organelles: [
        { type: OrganelleType.FOOD_VACUOLE, density: 0.8 },
        { type: OrganelleType.CONTRACTILE_VACUOLE, density: 0.5 },
        { type: OrganelleType.MITOCHONDRIA, density: 0.5 },
        { type: OrganelleType.RIBOSOME, density: 0.6 },
      ],
      cytoplasm: {
        color: { h: 270, s: 15, l: 85 },
        density: 0.4,
      },
      animation: {
        drift: 0.5,
        pulse: 0.3,
        morph: 2, // High morph for amoeba movement
      },
    };
  }

  /**
   * Create back button to return to menu
   */
  private createBackButton(): void {
    const container = this.add.container(30, 30);

    // Button background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 0.9);
    bg.fillRoundedRect(0, 0, 90, 36, 8);
    bg.lineStyle(2, 0x00d4ff, 0.5);
    bg.strokeRoundedRect(0, 0, 90, 36, 8);

    // Arrow and text
    const text = this.add.text(45, 18, '← Back', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#00d4ff',
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(90, 36);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(1000);

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x0a1628, 1);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(2, 0x00d4ff, 0.8);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x0a1628, 0.9);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(2, 0x00d4ff, 0.5);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00d4ff');
    });

    container.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  /**
   * Create bioluminescent deep-sea background
   */
  private createBackground(): void {
    const { width, height } = this.cameras.main;

    // Base deep ocean background
    const bg = this.add.graphics();
    bg.fillStyle(0x040810, 1);
    bg.fillRect(0, 0, width, height);

    // Deep radial gradient from center
    const radialGradient = this.add.graphics();
    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.max(width, height) * 0.8;

    for (let i = 40; i >= 0; i--) {
      const radius = (i / 40) * maxRadius;
      const alpha = 0.02 * (1 - i / 40);
      radialGradient.fillStyle(0x0d2847, alpha);
      radialGradient.fillCircle(centerX, centerY, radius);
    }

    // Bioluminescent glow spots
    const glowSpots = this.add.graphics();
    const glowPositions = [
      { x: width * 0.2, y: height * 0.3, color: 0x00d4ff, size: 200 },
      { x: width * 0.8, y: height * 0.6, color: 0x00ffc8, size: 180 },
      { x: width * 0.3, y: height * 0.8, color: 0xa855f7, size: 150 },
      { x: width * 0.7, y: height * 0.2, color: 0x00ff88, size: 160 },
    ];

    glowPositions.forEach(glow => {
      for (let i = 15; i >= 0; i--) {
        const radius = (i / 15) * glow.size;
        const alpha = 0.012 * (1 - i / 15);
        glowSpots.fillStyle(glow.color, alpha);
        glowSpots.fillCircle(glow.x, glow.y, radius);
      }
    });

    // Floating particles
    const particles = this.add.graphics();
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = Math.random() * 2 + 0.5;

      particles.fillStyle(0x00d4ff, 0.08);
      particles.fillCircle(x, y, size * 2);
      particles.fillStyle(0x00ffc8, 0.2);
      particles.fillCircle(x, y, size);
    }

    // Soft vignette
    const vignette = this.add.graphics();
    const corners = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: 0, y: height },
      { x: width, y: height },
    ];

    corners.forEach(corner => {
      for (let i = 10; i >= 0; i--) {
        const radius = (i / 10) * 300;
        const alpha = 0.06 * (1 - i / 10);
        vignette.fillStyle(0x000000, alpha);
        vignette.fillCircle(corner.x, corner.y, radius);
      }
    });
  }
}
